import {
  type ImportMap,
  initFederation,
  processRemoteInfo,
} from '@angular-architects/native-federation'

import type { AppConfig } from './app/shared/api/services/app-config/app-config.service'

type FederationManifest = Record<string, string>

// Remotes are only worth initialising when their feature flag is on. A remote
// listed in the manifest but disabled (or not yet deployed) in an environment
// must not produce startup errors — the host has to boot without it.
const REMOTE_ENABLED: Record<string, (config: AppConfig) => boolean> = {
  'chart-builder-ui': config => !!config.mfeFeatureFlags?.chartBuilderUi,
  'shared-library-ui': config =>
    !!config.mfeLibraryFeatureFlags?.enableMfeLibrary,
  'shared-feeds-ui': config => !!config.mfeFeedsFeatureFlags?.enableFeeds,
  'shared-notifications-ui': config =>
    !!config.mfeNotificationFeatureFlags?.enableNotifications,
  'shared-search-ui': config => !!config.mfeSearchFeatureFlags?.enableSearch,
}

const fetchJson = async <T>(path: string): Promise<T> => {
  // Resolve against the <base href> so the app also works when hosted under
  // a path prefix (e.g. /oden/), mirroring AppConfigService.
  const response = await fetch(new URL(path, document.baseURI), {
    cache: 'no-cache',
  })
  if (!response.ok) {
    throw new Error(`GET ${path} responded ${response.status}`)
  }
  return (await response.json()) as T
}

// Drop manifest entries whose feature flag is off. Remotes without a known
// flag mapping stay enabled. If the app config cannot be read, fall back to
// the full manifest — initFederation tolerates unreachable remotes anyway.
const loadEnabledRemotes = async (): Promise<FederationManifest> => {
  const manifest = await fetchJson<FederationManifest>(
    'federation.manifest.json'
  )
  try {
    const appConfig = await fetchJson<AppConfig>('config')
    return Object.fromEntries(
      Object.entries(manifest).filter(
        ([remoteName]) => REMOTE_ENABLED[remoteName]?.(appConfig) ?? true
      )
    )
  } catch (err) {
    console.warn(
      '[Host] Could not read app config to filter MFE remotes - initialising all remotes',
      err
    )
    return manifest
  }
}

// Shown when even the host bundle cannot be loaded (e.g. stale cached chunks
// after a deployment). Anything is better than a silent blank screen.
const showBootstrapError = () => {
  document.body.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;text-align:center;padding:1rem">
      <div>
        <h1 style="font-size:1.25rem">Something went wrong while loading the application</h1>
        <p>Please refresh the page. If the problem persists, clear your browser cache and try again.</p>
        <button style="padding:0.5rem 1rem;cursor:pointer" onclick="window.location.reload()">Refresh</button>
      </div>
    </div>`
}

// A failed ./bootstrap import usually means the browser used stale cached
// chunks from a previous deployment (the server answers missing chunk files
// with index.html, which then fails MIME checking). Reload once to pick up
// fresh files before surfacing an error.
const recoverFromBootstrapError = (err: unknown) => {
  console.error('[Host] Failed to bootstrap application', err)
  const retryKey = 'oden-bootstrap-retried'
  try {
    if (!sessionStorage.getItem(retryKey)) {
      sessionStorage.setItem(retryKey, '1')
      window.location.reload()
      return
    }
    sessionStorage.removeItem(retryKey)
  } catch {
    // sessionStorage unavailable - fall through to the error message
  }
  showBootstrapError()
}

const isLocalDev = ['localhost', '127.0.0.1', '::1'].includes(
  window.location.hostname
)

// The cacheTag busts HTTP caches for every remoteEntry.json (the host’s own and each MFE’s).
// Local dev is excluded so remoteEntry URLs remain stable when running on localhost.
const federationOptions: { cacheTag?: string } = isLocalDev
  ? {}
  : { cacheTag: Date.now().toString() }

// A remote that never answers (DNS black hole, firewalled host, an MFE not
// yet made public in an environment) must not hang forever: `fetch` has no
// built-in timeout, so without this a single unreachable remote can leave
// its init promise pending indefinitely. 8s is generous for a legitimate
// cold start over the public internet while keeping a dead remote from
// stalling anything that happens to be waiting on it.
const REMOTE_INIT_TIMEOUT_MS = 8000

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`timed out after ${timeoutMs}ms`)),
      timeoutMs
    )
    promise.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      err => {
        clearTimeout(timer)
        reject(err)
      }
    )
  })

// `initFederation` is called EXACTLY ONCE, and only for the host. Every call
// re-fetches the host's own `remoteEntry.json`, re-runs `processHostInfo`
// (which maps each shared singleton to a document-relative './<file>') and
// appends another `<script type="importmap-shim">`. Calling it once per remote
// therefore redeclared the same top-level specifiers several times over, and
// es-module-shims refuses to remap one it has already composed:
//
//   Rejected map override "@angular/animations" from
//   http://localhost:4300/_angular_animations.<hash>.js
//   to ./_angular_animations.<hash>.js
//
// - the same host file on both sides, once resolved against <base href> and
// once still raw. Being a race between the appended maps and es-module-shims
// locking resolution, it only threw sometimes: more enabled remotes and slower
// remote fetches made it likelier. Remotes now go through `processRemoteInfo`
// instead, which reads only that remote's own federation info and touches
// nothing belonging to the host.
const hostFederation = initFederation({}, federationOptions).catch(err =>
  console.error(
    '[Host] Host federation initialisation failed - continuing',
    err
  )
)

// Mirrors the runtime's own `appendImportMap`, which it doesn't export.
const appendImportMap = (importMap: ImportMap): void => {
  document.head.appendChild(
    Object.assign(document.createElement('script'), {
      type: 'importmap-shim',
      innerHTML: JSON.stringify(importMap),
    })
  )
}

// `initFederation` applies the cacheTag to the remote URLs it is handed;
// `processRemoteInfo` takes a plain URL, so do it here to keep remoteEntry.json
// cache-busting behaviour identical outside local dev.
const withCacheTag = (url: string): string => {
  const cacheTag = federationOptions.cacheTag
  if (!cacheTag) {
    return url
  }
  return `${url}${url.includes('?') ? '&' : '?'}t=${cacheTag}`
}

// Each enabled remote is processed on its own, independent call - not as one
// batch. `loadRemoteModule` treats a remote nothing has initialised as simply
// "unknown" and its callers already fall back to a friendly page (see
// app.routes.ts's loadMfeRoutesWithFallback), so a remote that times out here
// just stays unavailable: it cannot block the shell, and it cannot block or
// fail any other remote's init.
const initRemote = (remoteName: string, url: string): Promise<void> => {
  // The import map is appended as soon as the remote's info resolves, even if
  // the timeout below already gave up waiting on it - a slow remote that
  // eventually answers should still be usable. Unlike the host's map, a
  // remote's carries only its own `<remoteName>/...` imports plus scopes keyed
  // by its own baseUrl, so it can never override the host mappings.
  const processed = processRemoteInfo(withCacheTag(url), remoteName).then(
    importMap => appendImportMap(importMap)
  )

  return withTimeout(processed, REMOTE_INIT_TIMEOUT_MS).catch(err =>
    console.error(`[Host] Failed to initialise MFE '${remoteName}'`, err)
  )
}

// MFEs load in the background, after the shell starts booting - never before.
// The manifest/config fetches run in parallel with the host's own init, but no
// remote is processed until that init has resolved: it seeds the shared-
// dependency cache (`setExternalUrl`), so a remote reuses the host's copy of a
// singleton like @angular/core rather than racing it and pinning its own.
Promise.all([loadEnabledRemotes(), hostFederation])
  .then(([remotes]) =>
    Object.entries(remotes).forEach(([remoteName, url]) =>
      initRemote(remoteName, url)
    )
  )
  .catch(err =>
    console.error('[Host] Could not determine enabled MFE remotes', err)
  )

// The shell must never wait on any remote: it boots as soon as the host's
// own federation info (same-origin, fast) is processed.
hostFederation
  .then(() => import('./bootstrap'))
  .catch(recoverFromBootstrapError)
